import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import socketService from '../services/SocketService';
import { getActiveRoles, getDashboardPath, getProfilePath, hasCapability, canPerformAction } from '../utils/roleUtils';
import ApiService from '../services/api';

// Robust API base resolution that ensures '/api' is present
function resolveAuthApiBase() {
  const env = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL)
    ? process.env.REACT_APP_API_URL.trim()
    : '';

  let base = env;
  // Strict: no window.origin fallback — use explicit env or local backend
  if (!base || !/^https?:\/\//.test(base)) {
    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    const isLocalFront = /localhost:(3000|3001|3002|3003)/.test(origin || '');
    base = isLocalFront ? 'https://jobit-vniz.onrender.com/api' : (origin ? origin.replace(/\/+$/, '') + '/api' : 'https://jobit-vniz.onrender.com/api');
  }
  if (!/\/api(\/?$)/.test(base)) {
    base = base.replace(/\/+$/, '') + '/api';
  }
  return base.replace(/\/+$/, '');
}

const API_BASE_URL = resolveAuthApiBase();

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [userRole, setUserRole] = useState(null); // Kept for backward compatibility
  const [userCapabilities, setUserCapabilities] = useState({});
  const [activeRoles, setActiveRoles] = useState([]);
  const [primaryRole, setPrimaryRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAdmin, setIsAdmin] = useState(false);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(5); // default fallback to 5 minutes
  const lastActivityRef = useRef(Date.now());
  const refreshInFlightRef = useRef(false);

  // Lightweight JWT parser to extract exp for proactive refresh
  const parseJwtExp = useCallback((jwtToken) => {
    try {
      if (!jwtToken) return null;
      const base64Url = jwtToken.split('.')[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      const payload = JSON.parse(jsonPayload);
      return typeof payload.exp === 'number' ? payload.exp : null;
    } catch (_) {
      return null;
    }
  }, []);

  // Helper function to update user role states
  const updateUserRoleStates = (user) => {
    if (user?.capabilities) {
      // New flexible role system
      const roles = getActiveRoles(user.capabilities);
      const primary = user.primaryRole || roles[0] || 'general';
      
      setUserCapabilities(user.capabilities);
      setActiveRoles(roles);
      setPrimaryRole(primary);
      setUserRole(primary); // For backward compatibility
    } else {
      // Handle general users and legacy users
      const userType = user?.userType || user?.role || 'general';
      
      if (userType === 'general') {
        // General users can browse and book services, but need to complete wizards for other actions
        setUserRole('general');
        setPrimaryRole('general');
        setActiveRoles(['general']);
        setUserCapabilities({
          canBrowseJobs: true,
          canBrowseServices: true,
          canBookServices: false, // Requires job seeker wizard
          canApplyForJobs: false, // Requires job seeker wizard
          canProvideServices: false, // Requires employer wizard
          canPostJobs: false, // Requires employer wizard
          isAdmin: false
        });
      } else {
        // Legacy users with specific roles - normalize service_provider to employer
        let normalizedUserType = userType;
        if (userType === 'service_provider' || userType === 'serviceprovider') {
          normalizedUserType = 'employer';
        } else if (userType === 'job_seeker') {
          normalizedUserType = 'jobseeker';
        } else if (userType === 'general') {
          // General users get basic capabilities, can later choose specific roles
          normalizedUserType = 'general';
        }
        
        setUserRole(normalizedUserType);
        setPrimaryRole(normalizedUserType);
        setActiveRoles([normalizedUserType]);
        
        // Set capabilities based on user type
        if (normalizedUserType === 'general') {
          setUserCapabilities({
            canBrowseJobs: true,
            canBrowseServices: true,
            canApplyForJobs: false, // Can be enabled later when they choose job seeker role
            canBookServices: false,
            canProvideServices: false,
            canPostJobs: false, // Can be enabled later when they choose employer role
            isAdmin: false
          });
        } else {
          setUserCapabilities({
            canBrowseJobs: true,
            canBrowseServices: true,
            canApplyForJobs: normalizedUserType === 'jobseeker' || normalizedUserType === 'employer',
            canBookServices: false,
            canProvideServices: false,
            canPostJobs: normalizedUserType === 'employer',
            isAdmin: normalizedUserType === 'admin'
          });
        }
      }
    }
  };
  // Refresh access token (defined before apiCall to avoid no-use-before-define)
  const refreshAccessToken = useCallback(async () => {
    try {
      let refreshEndpoint = '/auth/refresh';
      let refreshTokenKey = 'refreshToken';
      let tokenKey = 'token';

      if (isAdmin) {
        refreshEndpoint = '/admin-auth/refresh';
        refreshTokenKey = 'adminRefreshToken';
        tokenKey = 'adminToken';
      }

      const refreshToken = localStorage.getItem(refreshTokenKey);
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_BASE_URL}${refreshEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const { accessToken, refreshToken: newRefreshToken } = data;
        localStorage.setItem(tokenKey, accessToken);
        if (newRefreshToken) {
          localStorage.setItem(refreshTokenKey, newRefreshToken);
        }
        setToken(accessToken);
        return accessToken;
      }
      
      throw new Error(data.message || 'Token refresh failed');
    } catch (error) {
      // If refresh fails, clear auth state locally to avoid logout-refresh loops
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminRefreshToken');
      setToken(null);
      setCurrentUser(null);
      setCurrentAdmin(null);
      setIsAdmin(false);
      try { socketService.disconnect(); } catch (e) {}
      throw error;
    }
  }, [isAdmin]);
  const apiCall = useCallback(async (endpoint, options = {}) => {
    // Normalize endpoint to avoid double '/api' and ensure leading slash
    const normalizeEndpoint = (ep) => {
      let e = ep || '';
      if (!e.startsWith('/')) e = '/' + e;
      // Collapse multiple slashes
      e = e.replace(/\/{2,}/g, '/');
      // If base ends with '/api' and endpoint starts with '/api/', strip one '/api'
      if (API_BASE_URL.endsWith('/api') && e.startsWith('/api/')) {
        e = e.slice(4); // remove leading '/api'
        if (!e.startsWith('/')) e = '/' + e;
      }
      return e;
    };

    const safeEndpoint = normalizeEndpoint(endpoint);
    const url = `${API_BASE_URL}${safeEndpoint}`;
    
    // Determine if this is an admin call
    const isAdminCall = isAdmin || (/^(\/admin)(\/|$)/.test(endpoint)
      || /^(\/admin-auth)(\/|$)/.test(endpoint)
      || /\/admin\//.test(endpoint)
      || /^(\/ads\/admin)(\/|$)/.test(endpoint)
      || /^(\/ad-banners\/admin)(\/|$)/.test(endpoint)
      || /^(\/users\/admin)(\/|$)/.test(endpoint)
      || /^(\/jobs\/admin)(\/|$)/.test(endpoint)
      || /^(\/approval)(\/|$)/.test(endpoint)
      || /^(\/review)(\/|$)/.test(endpoint)
      || /^(\/payments\/transactions)/.test(endpoint));
    
    // Determine which token to use
    let authToken = token;
    let tokenKey = 'token';
    let refreshKey = 'refreshToken';
    if (isAdminCall) {
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        authToken = adminToken;
        tokenKey = 'adminToken';
        refreshKey = 'adminRefreshToken';
      }
    }
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const contentType = response.headers.get('content-type');
      let data;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        if (!response.ok && response.status === 404) {
          const origin = typeof window !== 'undefined' ? window.location.origin : '';
          const altBase = origin ? origin.replace(':3001', ':5000').replace(/\/+$/, '') + '/api' : API_BASE_URL;
          const altUrl = `${altBase}${safeEndpoint}`;
          try {
            const altResp = await fetch(altUrl, config);
            const altCT = altResp.headers.get('content-type');
            if (altCT && altCT.includes('application/json')) {
              const altData = await altResp.json();
              if (altResp.ok) {
                return altData;
              }
              data = altData;
            } else {
              await altResp.text();
              if (altResp.ok) {
                return { success: true };
              }
              throw new Error('Request failed. Please try again later.');
            }
          } catch (_) {}
        }
        const __debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
        if (__debug) console.warn('[AuthContext] non-JSON response', { endpoint: safeEndpoint, status: response.status });
        if (!response.ok) {
          throw new Error('Request failed. Please try again later.');
        }
        data = { success: true, message: 'Request completed' };
      }

      if (!response.ok) {
        let errorMessage = data && data.message ? data.message : 'API request failed';
        const firstErr = Array.isArray(data?.errors) && data.errors.length > 0 ? data.errors[0] : null;
        if (firstErr && !/Invalid credentials/i.test(errorMessage)) {
          const label = firstErr.field || firstErr.param || 'field';
          const msg = firstErr.message || 'Invalid value';
          errorMessage = `${errorMessage}: ${label} - ${msg}`;
        }
        if (response.status === 401) {
          // Special handling for auth endpoints where we don't refresh
          if (endpoint === '/admin-auth/login' || endpoint === '/auth/login') {
            errorMessage = data.message || 'Invalid credentials. Please check your email and password.';
            if (isAdminCall) {
              localStorage.removeItem('adminToken');
              localStorage.removeItem('adminRefreshToken');
              setToken(null);
              setCurrentAdmin(null);
              setIsAdmin(false);
            } else {
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              setToken(null);
              setCurrentUser(null);
            }
          throw new Error(errorMessage);
        }
        
        // Skip refresh for logout/refresh endpoints to prevent loops
        const skipEndpoints = isAdminCall 
          ? ['/admin-auth/logout', '/admin-auth/refresh'] 
          : ['/auth/logout', '/auth/refresh'];
            
          if (skipEndpoints.some(ep => endpoint.startsWith(ep))) {
            if (isAdminCall) {
              localStorage.removeItem('adminToken');
              localStorage.removeItem('adminRefreshToken');
              setToken(null);
              setCurrentAdmin(null);
              setIsAdmin(false);
            } else {
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              setToken(null);
              setCurrentUser(null);
            }
            throw new Error(data.message || 'Unauthorized. Please log in again.');
          }
          
          // Attempt token refresh and retry the request once
          try {
            const newToken = await refreshAccessToken();
            const retryConfig = {
              ...config,
              headers: {
                ...config.headers,
                Authorization: `Bearer ${newToken}`,
              },
            };
            const retryResponse = await fetch(url, retryConfig);
            const retryContentType = retryResponse.headers.get('content-type');
            let retryData;
            if (retryContentType && retryContentType.includes('application/json')) {
              retryData = await retryResponse.json();
            } else {
              await retryResponse.text();
              const __debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
              if (__debug) console.warn('[AuthContext] non-JSON retry response', { endpoint, status: retryResponse.status });
              if (!retryResponse.ok) {
                throw new Error('Request failed. Please try again later.');
              }
              retryData = { success: true, message: 'Request completed' };
            }
            if (!retryResponse.ok) {
              // Refresh worked but request still unauthorized; clear tokens
              localStorage.removeItem(tokenKey);
              localStorage.removeItem(refreshKey);
              setToken(null);
              if (isAdminCall) {
                setCurrentAdmin(null);
                setIsAdmin(false);
              } else {
                setCurrentUser(null);
              }
              throw new Error(retryData.message || 'Unauthorized. Please log in again.');
            }
            return retryData;
          } catch (refreshError) {
            // Refresh failed; clear tokens and prompt re-login
            localStorage.removeItem(tokenKey);
            localStorage.removeItem(refreshKey);
            setToken(null);
            if (isAdminCall) {
              setCurrentAdmin(null);
              setIsAdmin(false);
            } else {
              setCurrentUser(null);
            }
            errorMessage = 'Unauthorized. Please log in again.';
            throw new Error(errorMessage);
          }
        } else {
          throw new Error(errorMessage);
        }
      }

      return data;
    } catch (error) {
      const __debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
      if (__debug) console.error('[AuthContext] API call failed', { endpoint: safeEndpoint, error });
      
      // Add specific handling for network errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error(`Network error: Unable to connect to server. Please check if the backend is running on ${API_BASE_URL}`);
      }
      
      throw error;
    }
  }, [token, isAdmin, refreshAccessToken]);

  // Load System Settings for session timeout (admin or public)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const settings = await ApiService.getSystemSettings();
        const timeout = settings?.security?.sessionTimeout;
        if (mounted && typeof timeout === 'number' && timeout > 0) {
          setSessionTimeoutMinutes(timeout);
        }
      } catch (e) {
        // Keep fallback if settings not reachable; avoid noisy logs in production
        const __debug2 = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
        if (__debug2) console.warn('[AuthContext] using fallback session timeout', sessionTimeoutMinutes);
      }
    })();
    return () => { mounted = false; };
  }, [currentUser, currentAdmin, sessionTimeoutMinutes]);

  // Register a new user
  async function register(email, password, name, userType = 'jobseeker', phone = '', additionalData = {}) {
    try {
      // Split name into firstName and lastName
      const nameParts = name.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');
      // Normalize userType to streamlined values
      let normalizedType = userType;
      if (normalizedType === 'job_seeker') normalizedType = 'jobseeker';
      if (normalizedType === 'service_provider' || normalizedType === 'serviceprovider') normalizedType = 'employer';
      if (!['jobseeker', 'employer'].includes(normalizedType)) {
        const err = new Error('Invalid user type. Please select jobseeker or employer.');
        err.response = { data: { message: err.message } };
        throw err;
      }

      const response = await apiCall('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
          phone,
          userType: normalizedType,
          ...additionalData
        }),
      });

      if (response.success) {
        const { user, accessToken, refreshToken } = response;
        
        // Store tokens
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        setToken(accessToken);
        
        // Set user data with uid mapping
        const userWithUid = {
          ...user,
          uid: user._id || user.uid // Map _id to uid for consistency
        };
        setCurrentUser(userWithUid);
        updateUserRoleStates(userWithUid);
        
        // Initialize Socket.IO connection
        socketService.connect(user._id, user.userType || 'general');
        
        return { user };
      }
      
      // If response is not successful, throw error with proper structure
      const error = new Error(response.message || 'Registration failed');
      error.response = { data: response }; // Attach response data for frontend error handling
      throw error;
    } catch (error) {
      // If it's a network error or other error, preserve the original structure
      if (!error.response && error.message) {
        const networkError = new Error(error.message);
        networkError.response = { data: { message: error.message } };
        throw networkError;
      }
      throw error;
    }
  }

  // Login user
  async function login(email, password) {
    try {
      const response = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (response.success) {
        const { user, accessToken, refreshToken } = response;
        
        // Store tokens
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        setToken(accessToken);

        // Ensure no lingering admin session interferes with user flows
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminRefreshToken');
        setIsAdmin(false);
        setCurrentAdmin(null);
        
        // Set user data with uid mapping
        const userWithUid = {
          ...user,
          uid: user._id || user.uid // Map _id to uid for consistency
        };
        setCurrentUser(userWithUid);
        updateUserRoleStates(userWithUid);
        
        // Initialize Socket.IO connection
        socketService.connect(user._id, user.userType);
        
        return { user };
      }
      
      throw new Error(response.message || 'Login failed');
    } catch (error) {
      // Enhanced error handling for better user experience
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        throw new Error('Invalid credentials. Please check your email and password.');
      } else if (error.message.includes('Network') || error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection.');
      } else if (error.message.includes('500')) {
        throw new Error('Server error. Please try again later.');
      }
      throw error;
    }
  }

  // Admin login
  async function adminLogin(email, password) {
    try {
      const response = await apiCall('/admin-auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (response.success) {
        const { admin, accessToken, refreshToken } = response;
        
        // Store tokens with admin prefix
        localStorage.setItem('adminToken', accessToken);
        localStorage.setItem('adminRefreshToken', refreshToken);
        
        // Clear any existing user tokens to prevent conflicts
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        
        // Set admin data and token state
        setToken(accessToken);
        setCurrentAdmin(admin);
        setIsAdmin(true);
        
        // Clear user state to prevent conflicts
        setCurrentUser(null);
        setUserRole(null);
        setUserCapabilities({});
        setActiveRoles([]);
        setPrimaryRole(null);
        
        return { admin };
      }
      
      throw new Error(response.message || 'Admin login failed');
    } catch (error) {
      // Enhanced error handling
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        throw new Error('Invalid admin credentials. Please check your email and password.');
      } else if (error.message.includes('Network') || error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection.');
      } else if (error.message.includes('500')) {
        throw new Error('Server error. Please try again later.');
      }
      throw error;
    }
  }

  // Logout user
  const logout = useCallback(async () => {
    try {
      // Call logout endpoint to invalidate refresh token
      if (token) {
        if (isAdmin) {
          await apiCall('/admin/logout', { method: 'POST' });
        } else {
          await apiCall('/auth/logout', { method: 'POST' });
        }
      }
    } catch (error) {
      const __debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
      if (__debug) console.error('[AuthContext] logout API failed', error);
    } finally {
      // Clear local storage and state regardless of API call success
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminRefreshToken');
      setToken(null);
      setCurrentUser(null);
      setCurrentAdmin(null);
      setIsAdmin(false);
      setUserRole(null);
      setUserCapabilities({});
      setActiveRoles([]);
      setPrimaryRole(null);
      
      // Disconnect Socket.IO
      socketService.disconnect();
    }
  }, [token, isAdmin, apiCall]);

  // Reset password
  async function resetPassword(email) {
    try {
      const response = await apiCall('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      if (response.success) {
        return response;
      }
      
      throw new Error(response.message || 'Password reset failed');
    } catch (error) {
      throw error;
    }
  }


  // Idle activity tracker and proactive token refresh
  useEffect(() => {
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'touchmove'];
    const markActive = () => { lastActivityRef.current = Date.now(); };
    activityEvents.forEach(evt => window.addEventListener(evt, markActive, { passive: true }));

    const interval = setInterval(async () => {
      const now = Date.now();
      const idleMs = now - lastActivityRef.current;
      const maxIdleMs = (sessionTimeoutMinutes || 5) * 60_000;

      // Auto-logout when user idle beyond threshold
      if ((localStorage.getItem('token') || localStorage.getItem('adminToken')) && idleMs >= maxIdleMs) {
        try {
          await logout();
          try { alert('You have been logged out due to  inactivity.'); } catch (_) {}
        } catch (e) {
          const __debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
          if (__debug) console.warn('[AuthContext] auto logout error', e);
        }
        return;
      }

      // Proactive refresh when near expiry and user is active
      const active = idleMs < maxIdleMs;
      const activeToken = localStorage.getItem('token') || localStorage.getItem('adminToken');
      const exp = parseJwtExp(activeToken);
      if (active && activeToken && exp) {
        const secondsLeft = exp - Math.floor(now / 1000);
        // Refresh when less than or equal to 60 seconds remaining
        if (secondsLeft <= 60 && !refreshInFlightRef.current) {
          refreshInFlightRef.current = true;
          try {
            await refreshAccessToken();
          } catch (e) {
            const __debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
            if (__debug) console.warn('[AuthContext] proactive refresh failed; will refresh on request', e);
          } finally {
            refreshInFlightRef.current = false;
          }
        }
      }
    }, 30_000); // check every 30 seconds

    return () => {
      clearInterval(interval);
      activityEvents.forEach(evt => window.removeEventListener(evt, markActive));
    };
  }, [sessionTimeoutMinutes, logout, refreshAccessToken, parseJwtExp]);

  // Toggle user capability
  async function toggleUserCapability(capability) {
    try {
      // Compute updated capabilities locally, then persist via profile PUT
      const prevCapabilities = (currentUser && currentUser.capabilities) || {};
      const updatedCapabilities = {
        ...prevCapabilities,
        [capability]: !prevCapabilities[capability]
      };

      const response = await apiCall('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify({ capabilities: updatedCapabilities }),
      });

      if (response.success) {
        // Update current user state with new capabilities
        setCurrentUser(prev => ({ ...prev, ...response.user }));
        updateUserRoleStates(response.user);
        return response;
      }
      
      throw new Error(response.message || 'Capability toggle failed');
    } catch (error) {
      throw error;
    }
  }

  // Update primary role
  async function updatePrimaryRole(newPrimaryRole) {
    try {
      const response = await apiCall('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify({ primaryRole: newPrimaryRole }),
      });

      if (response.success) {
        // Update current user state with new primary role
        setCurrentUser(prev => ({ ...prev, primaryRole: newPrimaryRole }));
        setPrimaryRole(newPrimaryRole);
        setUserRole(newPrimaryRole); // For backward compatibility
        return response;
      }
      
      throw new Error(response.message || 'Primary role update failed');
    } catch (error) {
      throw error;
    }
  }
  async function updateUserProfile(data) {
    try {
      // Map incoming wizard/profile payload to backend schema
      const { mapJobSeekerToBackend, mapEmployerToBackend } = await import('../utils/profileMapping');
      const type = (data?.userType || currentUser?.userType || '').toLowerCase();
      const normalizedType = type === 'job_seeker' ? 'jobseeker' : type;
      const mapped = normalizedType === 'employer'
        ? mapEmployerToBackend(data)
        : mapJobSeekerToBackend(data);

      const response = await apiCall('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(mapped),
      });

      if (response.success) {
        // Update current user state with new data
        setCurrentUser(prev => ({ ...prev, ...response.user }));
        return response;
      }
      
      throw new Error(response.message || 'Profile update failed');
    } catch (error) {
      throw error;
    }
  }

  // Check authentication status on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedAdminToken = localStorage.getItem('adminToken');
      
      if (storedAdminToken) {
        try {
          // Set token first to ensure API calls work
          setToken(storedAdminToken);
          
          // Verify admin token and get admin data
          const response = await apiCall('/admin-auth/profile');
          
          if (response.success) {
            setCurrentAdmin(response.admin);
            setIsAdmin(true);
            
            // Clear user state to prevent conflicts
            setCurrentUser(null);
            setUserRole(null);
            setUserCapabilities({});
            setActiveRoles([]);
            setPrimaryRole(null);
          } else {
            // Admin token is invalid, clear it
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminRefreshToken');
            setToken(null);
            setIsAdmin(false);
            setCurrentAdmin(null);
          }
        } catch (error) {
          const __debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
          if (__debug) console.error('[AuthContext] admin token verification failed', error);
          // Admin token verification failed
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminRefreshToken');
          setToken(null);
          setIsAdmin(false);
          setCurrentAdmin(null);
        }
      } else if (storedToken) {
        try {
          // Set token first to ensure API calls work
          setToken(storedToken);
          
          // Verify token and get user data
          const response = await apiCall('/auth/profile');
          
          if (response.success) {
            const userWithUid = {
              ...response.user,
              uid: response.user._id || response.user.uid // Map _id to uid for consistency
            };
            setCurrentUser(userWithUid);
            updateUserRoleStates(userWithUid);
            
            // Initialize Socket.IO connection
            socketService.connect(userWithUid._id, userWithUid.userType);
          } else {
            // Token is invalid, clear it
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            setToken(null);
          }
        } catch (error) {
          const __debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
          if (__debug) console.error('[AuthContext] token verification failed', error);
          // Token verification failed, try to refresh
          try {
            await refreshAccessToken();
          } catch (refreshError) {
            const __debug2 = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
            if (__debug2) console.error('[AuthContext] token refresh failed', refreshError);
            // Refresh also failed, clear everything
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            setToken(null);
          }
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, [apiCall, refreshAccessToken]);

  const value = {
    currentUser,
    currentAdmin,
    isAdmin,
    userRole,
    userCapabilities,
    activeRoles,
    primaryRole,
    token,
    register,
    login,
    adminLogin,
    logout,
    resetPassword,
    updateUserProfile,
    toggleUserCapability,
    updatePrimaryRole,
    refreshAccessToken,
    loading,
    apiCall,
    // Backward compatibility - alias currentUser as user
    user: currentUser,
    // Utility functions
    hasCapability: (capability) => hasCapability(currentUser, capability),
    canPerformAction: (action) => canPerformAction(currentUser, action),
    getDashboardPath: () => getDashboardPath(primaryRole),
    getProfilePath: () => getProfilePath(primaryRole)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}